import Image from "next/image";
import { AnimatePresence, motion, type PanInfo } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect } from "react";

import Button from "@/blueprint/components/Button/Button";
import Dialog from "@/blueprint/components/Dialog/Dialog";
import IconButton from "@/blueprint/components/IconButton/IconButton";
import Text from "@/blueprint/components/Text/Text";
import { MotionEasing, seconds } from "@/blueprint/tokens/Motion";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { optimizedPoster } from "@/lib/optimizedPoster";
import { shopifyImageUrl } from "@/lib/shopifyImage";
import { swipeStep } from "@/lib/momentum";
import { cn } from "@/lib/utils";
import {
  ADD_ON_CLIPS,
  type AddOnClipsId,
} from "@/views/shop/featureTabsContent";
import { ClipSequence } from "@/views/shop/sections/configurator/FeatureTabs";
import type { AddOnId, ResolvedAddOn } from "@/views/shop/addOns";
import {
  ADD_ON_DETAILS,
  CONFIGURATOR,
  CONFIGURATOR_VISUALS,
} from "@/views/shop/shopContent";

/*
 * An add-on's details, after Tesla's accessory dialogs: its photo beside its
 * name, Shopify's description and one button that adds or removes it. Dots
 * underneath, swipes, and the neighbouring add-ons peeking in, page through
 * the rest without closing, looping round from the last to the first.
 *
 * Below `sd`, Tesla's phone deck (tesla.com/model3/design at 375): one card
 * 24px in from either side, as tall as the screen less its dots, the photo
 * over the text and the whole card scrolling, the next card's edge showing
 * at the right, a translucent close in its corner, on a dark scrim. From
 * `sd`, a wide card with its neighbours half in view at either side.
 */

// Below `sd`: the card's width (24px in from either side, 560 at most); its
// height follows its content, short copy and the CTA, up to the screen less
// the dots' 48 and 20 above (760 on tablets). The deck centres on the screen.
const STACKED_CARD =
  "w-[min(calc(100vw-48px),560px)] max-h-[calc(100svh-68px)] tab:max-h-[min(calc(100svh-68px),760px)]";

const FADE_MS = 200;

const SCRIM_FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-white";

/** Whether an add-on has a photo or animation to show. */
const hasVisual = (addOn: ResolvedAddOn) =>
  addOn.id === "builder-kit" ||
  addOn.id in ADD_ON_CLIPS ||
  addOn.imageUrls.length > 0;

/** The clips an add-on plays in place of a photo, if it has any. */
const clipsFor = (addOn: ResolvedAddOn) =>
  addOn.id in ADD_ON_CLIPS ? ADD_ON_CLIPS[addOn.id as AddOnClipsId] : null;

/**
 * The add-on's photo, or Builder Kit's animation, on the page's ground; an
 * add-on with neither shows its name, which only a peek ever needs.
 */
const AddOnVisual = ({
  addOn,
  reducedMotion,
  className,
}: {
  addOn: ResolvedAddOn;
  reducedMotion: boolean;
  className?: string;
}) => {
  const imageUrl = addOn.imageUrls[0];
  const clips = clipsFor(addOn);
  const firstClip = clips?.[0];

  return (
    <div
      className={cn(
        "relative isolate overflow-hidden bg-surface-page",
        className,
      )}
    >
      {clips !== null && firstClip !== undefined ? (
        // A peek holds still on the first clip's poster; the open add-on
        // plays its clips through.
        reducedMotion && firstClip.kind === "video" ? (
          <Image
            src={firstClip.poster}
            alt=""
            fill
            sizes="568px"
            className="pointer-events-none select-none object-cover"
          />
        ) : (
          <ClipSequence clips={clips} paused={reducedMotion} />
        )
      ) : addOn.id === "builder-kit" ? (
        <video
          src={CONFIGURATOR_VISUALS.builderKit.video}
          poster={optimizedPoster(
            CONFIGURATOR_VISUALS.builderKit.poster.src,
            1920,
          )}
          autoPlay={!reducedMotion}
          loop={!reducedMotion}
          muted
          playsInline
          aria-hidden
          className="pointer-events-none absolute inset-0 size-full object-contain mix-blend-multiply"
        />
      ) : imageUrl === undefined ? (
        <span className="absolute inset-0 flex items-center justify-center p-xl text-center">
          <Text variant="h5" tone="secondary" as="span">
            {addOn.name}
          </Text>
        </span>
      ) : (
        // The name beside it says what this is; the photo adds nothing to read.
        <img
          src={shopifyImageUrl(imageUrl, 1200)}
          alt=""
          draggable={false}
          className="pointer-events-none absolute inset-0 size-full select-none object-contain p-l mix-blend-multiply"
        />
      )}
    </div>
  );
};

/**
 * A neighbouring add-on, half in view at the dialog's side from `sd`, that
 * pages to it. Below `sd` only the next one shows, a card's edge at the
 * screen's right as Tesla's deck has it; the previous is off the screen.
 */
const Peek = ({
  addOn,
  label,
  direction,
  reducedMotion,
  onClick,
}: {
  addOn: ResolvedAddOn | undefined;
  label: string;
  direction: "previous" | "next";
  reducedMotion: boolean;
  onClick: () => void;
}) => {
  const stackedClassName =
    direction === "next" ? cn("block", STACKED_CARD) : "hidden";

  // An empty side keeps its room, so the open add-on stays centred.
  if (addOn === undefined) {
    return (
      <div
        aria-hidden
        className="hidden h-[568px] w-[488px] shrink-0 sd:block"
      />
    );
  }

  const Chevron = direction === "previous" ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "group relative shrink-0 overflow-hidden rounded-m bg-surface-dialog sd:block sd:h-[568px] sd:w-[488px] sd:bg-transparent",
        stackedClassName,
        SCRIM_FOCUS,
      )}
    >
      <AddOnVisual
        addOn={addOn}
        reducedMotion
        className="absolute inset-x-0 top-0 [aspect-ratio:1] motion-reduce:transition-none tab:[aspect-ratio:4/3] sd:inset-0 sd:opacity-60 sd:transition-opacity sd:duration-quick sd:[aspect-ratio:auto] sd:group-hover:opacity-80"
      />
      {/* Our white icon button, drawn rather than nested: the peek itself is
          the button, so its hover inverts the chevron as the button's own
          would. */}
      <IconButton
        decorative
        className={cn(
          "absolute top-1/2 hidden -translate-y-1/2 sd:flex",
          // 24px in from the edge nearest the open add-on, so it shows
          // however little of the peek the window leaves in view.
          direction === "next" ? "left-l" : "right-l",
          "group-hover:bg-brand-black group-hover:text-brand-white",
          reducedMotion && "transition-none",
        )}
      >
        <Chevron aria-hidden className="size-l" strokeWidth={1.5} />
      </IconButton>
    </button>
  );
};

export const AddOnDetailsDialog = ({
  addOns,
  openId,
  selectedIds,
  onShow,
  onToggle,
  onClose,
}: {
  addOns: readonly ResolvedAddOn[];
  /** The add-on on show; null keeps the dialog closed. */
  openId: AddOnId | null;
  selectedIds: ReadonlySet<AddOnId>;
  /** Page to another add-on without closing. */
  onShow: (id: AddOnId) => void;
  onToggle: (addOn: ResolvedAddOn) => void;
  onClose: () => void;
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const index = addOns.findIndex(({ id }) => id === openId);
  const addOn = addOns[index];
  // The deck loops: past the last add-on comes the first again, and back
  // from the first, the last. One add-on alone has no neighbours.
  const count = addOns.length;
  const previous =
    count > 1 && index >= 0 ? addOns[(index - 1 + count) % count] : undefined;
  const next =
    count > 1 && index >= 0 ? addOns[(index + 1) % count] : undefined;
  const isOpen = addOn !== undefined;

  // The arrow keys page, as the dots and peeks do.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && previous !== undefined)
        onShow(previous.id);
      if (event.key === "ArrowRight" && next !== undefined) onShow(next.id);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, previous, next, onShow]);

  if (addOn === undefined) return null;

  const isLocked = addOn.isIncluded;
  const isSelected = isLocked || selectedIds.has(addOn.id);
  const canToggle = !isLocked && addOn.availableForSale;
  const headingId = `add-on-details-${addOn.id}`;
  const fade = reducedMotion
    ? { duration: 0 }
    : { duration: seconds(FADE_MS), ease: "easeOut" as const };

  const handleDragEnd = (_event: unknown, info: PanInfo) => {
    const swipe = swipeStep(info.offset.x, info.velocity.x);
    if (swipe === 1 && next !== undefined) onShow(next.id);
    else if (swipe === -1 && previous !== undefined) onShow(previous.id);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby={headingId}
      dismissOnBackdropClick
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-0 overflow-hidden bg-tint-black-60 sd:justify-center sd:gap-l sd:bg-tint-pure-black-20 sd:p-xl sd:backdrop-blur-md"
    >
      {/* Below `sd` the open card sits 24px in (centred at 560 on tablets),
          the next one 8px past it; from `sd` the three are centred. */}
      <div className="pointer-events-none flex w-full items-stretch justify-start gap-xs pl-[max(24px,calc((100vw-560px)/2))] sd:items-center sd:justify-center sd:gap-[54px] sd:pl-0 [&>*]:pointer-events-auto">
        <Peek
          addOn={previous}
          label={ADD_ON_DETAILS.previous}
          direction="previous"
          reducedMotion={reducedMotion}
          onClick={() => previous !== undefined && onShow(previous.id)}
        />

        <motion.div
          // The card opens from 0.96 on a strong ease-out: quick to arrive,
          // soft to settle. Centred, as a modal is.
          initial={reducedMotion ? false : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : { duration: 0.24, ease: [...MotionEasing.outStrong] }
          }
          drag={addOns.length > 1 ? "x" : false}
          dragDirectionLock
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.15}
          onDragEnd={handleDragEnd}
          className={cn(
            "relative flex shrink-0 touch-pan-y flex-col overflow-y-auto overscroll-contain rounded-m bg-surface-dialog sd:h-[568px] sd:flex-row sd:overflow-hidden",
            STACKED_CARD,
            hasVisual(addOn) ? "sd:w-[908px]" : "sd:w-[440px]",
          )}
        >
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={addOn.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={fade}
              className="flex w-full flex-col sd:flex-row"
            >
              {/* Without a photo the card is its text alone, narrower. */}
              {hasVisual(addOn) && (
                <AddOnVisual
                  addOn={addOn}
                  reducedMotion={reducedMotion}
                  className="w-full shrink-0 [aspect-ratio:1] tab:[aspect-ratio:4/3] sd:h-full sd:w-[568px] sd:[aspect-ratio:auto]"
                />
              )}
              {/* Tesla's text block: 24 in, with room above the title. */}
              <div className="flex min-h-0 flex-1 flex-col gap-m px-l pb-l pt-[48px] sd:gap-l sd:px-[26px] sd:pb-[26px] sd:pt-[90px]">
                <Text variant="h6" as="h2" className="sd:text-h5">
                  <span id={headingId}>{addOn.name}</span>
                </Text>
                {/* Scrolls within the card from `sd`, where the card's height
                    is fixed; below it the whole card scrolls. */}
                {addOn.detailsHtml === null ? (
                  <Text
                    variant="paragraph-md"
                    tone="secondary"
                    as="p"
                    className="sd:min-h-0 sd:overflow-y-auto"
                  >
                    {addOn.description}
                  </Text>
                ) : (
                  <Text
                    variant="paragraph-md"
                    tone="secondary"
                    as="div"
                    className="sd:min-h-0 sd:overflow-y-auto"
                  >
                    <div
                      className="flex flex-col gap-s [&_ol]:list-decimal [&_ol]:pl-l [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-l"
                      // Sanitized to text-level tags when resolved (addOns).
                      dangerouslySetInnerHTML={{ __html: addOn.detailsHtml }}
                    />
                  </Text>
                )}
                {/* Our primary button at the card's foot. */}
                <Button
                  disabled={!canToggle}
                  onClick={() => onToggle(addOn)}
                  className="mt-auto w-full shrink-0"
                >
                  {isLocked
                    ? CONFIGURATOR.includedLabel
                    : isSelected
                      ? ADD_ON_DETAILS.remove
                      : ADD_ON_DETAILS.add}
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Our icon button, over the photo's corner. */}
          <IconButton
            label={ADD_ON_DETAILS.close}
            onClick={onClose}
            className="absolute right-m top-m z-10 sd:right-l sd:top-l"
          >
            <X aria-hidden className="size-l" strokeWidth={1.5} />
          </IconButton>
        </motion.div>

        <Peek
          addOn={next}
          label={ADD_ON_DETAILS.next}
          direction="next"
          reducedMotion={reducedMotion}
          onClick={() => next !== undefined && onShow(next.id)}
        />
      </div>

      {addOns.length > 1 && (
        // Below `sd` the dots sit in the 48px under the card, Tesla's.
        <div className="flex h-[48px] items-center sd:h-auto">
          <div
            role="group"
            aria-label={ADD_ON_DETAILS.pagination}
            className="flex items-center"
          >
            {addOns.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-label={ADD_ON_DETAILS.show(item.name)}
                aria-current={item.id === addOn.id ? "true" : undefined}
                onClick={() => onShow(item.id)}
                // A 24px target around each 8px dot.
                className={cn(
                  "flex size-[24px] items-center justify-center rounded-full",
                  SCRIM_FOCUS,
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    "size-xs rounded-full transition-colors duration-quick motion-reduce:transition-none",
                    item.id === addOn.id
                      ? "bg-brand-white"
                      : "bg-tint-white-70 hover:bg-brand-white",
                  )}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </Dialog>
  );
};
